import React, { useEffect, useState } from 'react';
import { ethers } from 'ethers';
import { useContracts } from '../../hooks/useContract';
import { useUserData } from '../../hooks/useUserData';
import { useWeb3 } from '../../context/Web3Context';
import { useNotify } from '../../context/NotificationContext';
import { formatUsdc, formatBps, parseUsdc, bnToNumber, formatTimestamp } from '../../utils/format';
import { PACKAGE_NAMES, INV_TYPES, NUM_PACKAGES } from '../../utils/constants';
import { NetworkGuard } from '../wallet/NetworkGuard';
import { InlineLoader, ButtonSpinner } from '../common/Loader';
import { EmptyState, Alert } from '../common/StatBox';
import { parseError } from '../../utils/errors';
import { ADDR } from '../../config/contracts';
import { readStoredRef, clearStoredRef } from '../../utils/referral';

export const Invest = () => (
  <NetworkGuard>
    <InvestInner />
  </NetworkGuard>
);

const InvestInner = () => {
  const [tab, setTab] = useState('packages');

  return (
    <div>
      <div className="mtabs">
        <button className={`mtab ${tab === 'packages' ? 'active' : ''}`} onClick={() => setTab('packages')}>📦 Packages</button>
        <button className={`mtab ${tab === 'mine' ? 'active' : ''}`} onClick={() => setTab('mine')}>💎 My Investments</button>
      </div>

      {tab === 'packages' && <PackagesTab />}
      {tab === 'mine' && <MyInvestmentsTab />}
    </div>
  );
};

const PackagesTab = () => {
  const { account } = useWeb3();
  const { viewsR, logicW, usdcR, usdcW } = useContracts();
  const { isRegistered, refresh } = useUserData();
  const notify = useNotify();

  const [packages, setPackages] = useState(null);
  const [balance, setBalance]   = useState(null);
  const [allowance, setAllowance] = useState(null);
  const [referrerId, setReferrerId] = useState('');
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [busyLabel, setBusyLabel] = useState('');
  const [reload, setReload] = useState(0);

  useEffect(() => {
    // Load referrer from sessionStorage (captured at app boot from ?ref= URL param).
    // Fall back to reading the URL directly in case sessionStorage was blocked.
    if (isRegistered) return;
    const stored = readStoredRef();
    const urlRef = new URLSearchParams(window.location.search).get('ref');
    const r = stored || urlRef || '';
    if (r) setReferrerId(r);
  }, [isRegistered]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!viewsR) return;
      try {
        const pkgs = await viewsR.getAllPackages();
        if (cancelled) return;
        setPackages(pkgs);

        if (account && usdcR) {
          const [b, a] = await Promise.all([
            usdcR.balanceOf(account),
            usdcR.allowance(account, ADDR.storage)
          ]);
          if (cancelled) return;
          setBalance(b);
          setAllowance(a);
        }
      } catch (e) {}
    };
    load();
    return () => { cancelled = true; };
  }, [viewsR, usdcR, account, reload]);

  const approveAndInvest = async (pkgIdx) => {
    const price = packages.prices[pkgIdx];
    if (!balance || balance.lt(price)) {
      notify.error(`Insufficient USDC balance (need $${formatUsdc(price)})`);
      return;
    }
    setSelected(pkgIdx);
    setBusy(true);
    try {
      if (!allowance || allowance.lt(price)) {
        setBusyLabel('Approving...');
        const ap = await usdcW.approve(ADDR.storage, ethers.constants.MaxUint256);
        notify.info('Approval submitted...');
        await ap.wait();
        notify.success('USDC approved');
      }
      setBusyLabel(isRegistered ? 'Investing...' : 'Registering...');
      const tx = isRegistered
        ? await logicW.invest(pkgIdx)
        : await logicW.registerAndInvest(referrerId ? ethers.BigNumber.from(referrerId) : ethers.constants.Zero, pkgIdx);
      notify.info('Transaction submitted...');
      await tx.wait();
      notify.success(isRegistered ? 'Investment successful!' : 'Registered & invested!');
      if (!isRegistered) clearStoredRef();
      refresh();
      setReload((r) => r + 1);
    } catch (e) {
      notify.error(parseError(e));
    } finally {
      setBusy(false);
      setBusyLabel('');
      setSelected(null);
    }
  };

  if (!packages) return <InlineLoader text="Loading packages..." />;

  return (
    <div>
      {balance && (
        <div className="lifetime-card" style={{ background: 'linear-gradient(90deg, rgba(0,229,255,0.08), rgba(0,255,157,0.05))', borderColor: 'var(--border-strong)' }}>
          <div className="lifetime-icon" style={{ background: 'var(--grad-cyan)' }}>💰</div>
          <div className="lifetime-body">
            <div className="lifetime-label">Your USDC Balance</div>
            <div className="lifetime-amount" style={{ color: 'var(--neon-cyan)' }}>${formatUsdc(balance)}</div>
          </div>
        </div>
      )}

      {!isRegistered && (
        <div className="section-card">
          <div className="section-card-title">
            <span><span className="icon">🔗</span> Referrer</span>
          </div>
          <label className="input-label">Referrer ID (optional)</label>
          <input
            className="input"
            type="number"
            placeholder="e.g. 12345"
            value={referrerId}
            onChange={(e) => setReferrerId(e.target.value)}
          />
          <Alert type="info">Your referrer earns level income from your activity.</Alert>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {Array.from({ length: NUM_PACKAGES }).map((_, i) => {
          const price = packages.prices[i];
          const roi = packages.rois[i];
          const periods = packages.periods[i];
          const totalRoi = bnToNumber(roi) * bnToNumber(periods);
          const isPremium = i >= 7;
          const isBusy = busy && selected === i;

          return (
            <div key={i} className={`pkg-mini-card ${isPremium ? 'premium' : ''}`}>
              <div className="pkg-mini-header">
                <div>
                  <div className="pkg-mini-name">{PACKAGE_NAMES[i] || `Package ${i}`}</div>
                  <div className="pkg-mini-price">
                    ${formatUsdc(price)}<span className="curr">USDC</span>
                  </div>
                </div>
                <span className="pkg-mini-tag">{isPremium ? '👑 Premium' : `Level ${i + 1}`}</span>
              </div>

              <div className="pkg-mini-details">
                <span>⚡ {formatBps(roi)}/day</span>
                <span>📅 {bnToNumber(periods)} days</span>
                <span>📈 {(totalRoi / 100).toFixed(0)}% ROI</span>
                <span>🎯 4x cap</span>
              </div>

              <button
                className={`btn ${isPremium ? 'btn-gold' : 'btn-primary'} btn-full`}
                onClick={() => approveAndInvest(i)}
                disabled={busy}
              >
                {isBusy && <ButtonSpinner />}
                {isBusy ? busyLabel : (isRegistered ? 'Invest Now' : 'Register & Invest')}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const MyInvestmentsTab = () => {
  const { userId, isRegistered } = useUserData();
  const { viewsR } = useContracts();
  const [invs, setInvs] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!viewsR || !isRegistered || !userId) return;
      try {
        const list = await viewsR.getAllUserInvestments(userId);
        if (cancelled) return;
        setInvs(list);
      } catch (e) {}
    };
    load();
    return () => { cancelled = true; };
  }, [viewsR, isRegistered, userId]);

  if (!isRegistered) {
    return <EmptyState icon="💎" title="Not registered yet" text="Pick a package from the Packages tab to get started." />;
  }
  if (!invs) return <InlineLoader />;
  if (invs.length === 0) return <EmptyState icon="📭" title="No investments" text="Head to Packages to create your first one." />;

  return (
    <div>
      {invs.map((inv, i) => {
        const incRcv = Number(inv.totalIncomeReceived.toString());
        const cap = Number(inv.maxTotalIncome.toString());
        const pct = cap > 0 ? (incRcv / cap) * 100 : 0;
        const type = bnToNumber(inv.investmentType);

        return (
          <div key={i} className={`inv-mini ${!inv.active ? 'inactive' : ''}`}>
            <div className="inv-mini-header">
              <div>
                <div className="inv-mini-amt">${formatUsdc(inv.amount)}</div>
                <div className="inv-mini-pkg">{PACKAGE_NAMES[inv.packageId] || `Package ${inv.packageId}`}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span className={`badge ${inv.active ? 'badge-green' : 'badge-muted'}`}>
                  {inv.active ? 'Active' : 'Closed'}
                </span>
                <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 4 }}>
                  {formatTimestamp(inv.timestamp)}
                </div>
              </div>
            </div>

            <div className="rank-req-row">
              <span className="k">Type</span>
              <span className="v" style={{ color: 'var(--text-primary)' }}>{INV_TYPES[type] || 'Real'}</span>
            </div>
            <div className="rank-req-row">
              <span className="k">ROI Earned</span>
              <span className="v" style={{ color: 'var(--neon-cyan)' }}>${formatUsdc(inv.roiEarned)}</span>
            </div>
            <div className="rank-req-row">
              <span className="k">Income Received</span>
              <span className="v" style={{ color: 'var(--neon-green)' }}>${formatUsdc(inv.totalIncomeReceived)}</span>
            </div>
            <div className="rank-req-row">
              <span className="k">Max Income (4x)</span>
              <span className="v">${formatUsdc(inv.maxTotalIncome)}</span>
            </div>

            <div className="inv-mini-progress-lbl">
              <span>Ceiling Progress</span>
              <span style={{ color: 'var(--neon-cyan)', fontWeight: 700 }}>{pct.toFixed(1)}%</span>
            </div>
            <div className="inv-progress-track">
              <div className="inv-progress-fill" style={{ width: `${Math.min(100, pct)}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};
